import { Award, Users, ShieldCheck, GraduationCap, BadgeCheck, Heart, Scale, HandHeart, Building2 } from "lucide-react";

import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SmartImage } from "@/components/SmartImage";
import { CtaBand } from "@/components/Section";
import { IMAGES } from "@/data/site";

// Approved content — reproduced verbatim.
const SECTIONS = [
  {
    icon: Award,
    title: "Established Experience",
    body: "Through the last thirty seven years Precision Maintenance has provided both commercial cleaning services and countless opportunities to scores of associates. We are most proud that we’ve witnessed in some cases generations of associates progress through the ranks and build meaningful lives. Our longest tenured employees are still with us after thirty and twenty five years respectively. Their dedication has proven the foundation of stability and growth. Our bilingual community employee base has been developed through regular training, skill improvement, performance evaluations, and compassion.",
    image: IMAGES.officeMop,
    alt: "Long-tenured Precision Maintenance associates working as a team",
  },
  {
    icon: Users,
    title: "Referral-Based Hiring",
    body: "We historically rely on referrals from trusted associates and their family members rather than traditional help-wanted advertising.",
    image: IMAGES.handshake,
    alt: "A trusted referral and handshake between associates",
  },
  {
    icon: ShieldCheck,
    title: "Screening & Reliability",
    body: "Associates are hand-picked for dependability, reliability, conscientiousness, work ethic, and attendance. References are checked, background checks are performed and site performance is continually evaluated.",
    image: null,
  },
  {
    icon: GraduationCap,
    title: "Site-Specific Training",
    body: "Each associate is initially given one and a half weeks of training at the customer’s site before an associate is deemed preliminarily ready. Afterward we monitor for promotion or reassignment.",
    image: IMAGES.corridor,
    alt: "On-site training in a commercial facility",
  },
  {
    icon: BadgeCheck,
    title: "Licensed, Bonded & Insured",
    body: "Precision Maintenance1 LLC is licensed, bonded, and insured, with information available upon request.",
    image: null,
  },
];

const CredibilityRow = ({ section, index }) => {
  const dark = index % 2 === 1;
  const Icon = section.icon;
  const flip = index % 2 === 1;
  const hasImage = Boolean(section.image);

  return (
    <section data-testid={`why-section-${index}`} className={dark ? "bg-cool-gray" : "bg-white"}>
      <div className="container-px mx-auto max-w-[1500px] py-20 lg:py-28">
        <div className={`grid items-center gap-12 ${hasImage ? "lg:grid-cols-2 lg:gap-20" : ""}`}>
          {/* Media */}
          {hasImage && (
            <Reveal className={flip ? "lg:order-2" : "lg:order-1"}>
              <SmartImage src={section.image} alt={section.alt} ratio="aspect-[4/3]" imgClassName="hover:scale-105" />
            </Reveal>
          )}

          {/* Content */}
          <div className={hasImage ? (flip ? "lg:order-1" : "lg:order-2") : "mx-auto max-w-4xl text-center"}>
            <Reveal>
              <span className={`inline-flex h-14 w-14 items-center justify-center bg-navy text-brand-green ${hasImage ? "" : "mx-auto"}`}>
                <Icon size={26} strokeWidth={1.7} />
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-display text-3xl font-bold leading-[1.03] tracking-tight text-navy sm:text-4xl lg:text-5xl">
                {section.title}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className={`mt-6 text-lg leading-relaxed text-steel ${hasImage ? "" : "mx-auto max-w-2xl"}`}>
                {section.body}
              </p>
            </Reveal>
            {section.note && (
              <Reveal delay={0.15}>
                <p className="mt-4 text-xs uppercase tracking-[0.18em] text-steel/70">{section.note}</p>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const VALUES = [
  {
    icon: Heart,
    title: "Management Philosophy",
    body: "We believe our associates are among our most valued resources and our customers are our lifeblood. When employees are treated well, customers are treated well. The company philosophy emphasizes fair pay, opportunities for improvement, respect, honesty, integrity, initiative, and stable work.",
  },
  {
    icon: Scale,
    title: "The Golden Rule",
    body: "Treat people with the same respect with which you wish to be treated and extend that same care and respect to extend to customers.",
  },
  {
    icon: HandHeart,
    title: "Supporting Immigrant Families",
    body: "Precision Maintenance is providing steady region employment, attracting conscientious, dependable associates and working to build opportunities for their families and children.",
  },
];

const REFERRALS = [
  { name: "Penske Automotive", region: "NY, NJ,CT" },
  { name: "Kollective Auto Group", region: "NJ, NY" },
  { name: "Celebrity Auto Group", region: "NJ, NY" },
  { name: "Port Authority of NY & NJ", region: "" },
  { name: "OCS Builders Group", region: "" },
];

const ACCREDITATIONS = ["NJ MBE certified", "Certified Port of Authority MWSBE", "Board Member - Literacy New Jersey"];

const PeopleAndValues = () => (
  <section id="people-values" data-testid="people-values" className="scroll-mt-24 bg-white">
    <div className="container-px mx-auto max-w-[1500px] py-20 lg:py-28">
      <Reveal>
        <span className="overline">Our People & Values</span>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-navy sm:text-4xl lg:text-5xl">
          People first, always.
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-px overflow-hidden border border-border bg-border lg:grid-cols-3">
        {VALUES.map((v, i) => {
          const Icon = v.icon;
          return (
            <Reveal key={v.title} delay={i * 0.08} className="bg-white">
              <div className="flex h-full flex-col gap-5 p-8 lg:p-10">
                <span className="inline-flex h-12 w-12 items-center justify-center bg-navy text-brand-green">
                  <Icon size={22} strokeWidth={1.7} />
                </span>
                <h3 className="font-display text-xl font-bold tracking-tight text-navy">{v.title}</h3>
                <p className="text-base leading-relaxed text-steel">{v.body}</p>
              </div>
            </Reveal>
          );
        })}
      </div>

      <div className="mt-20 grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
        <div>
          <Reveal>
            <span className="inline-flex h-12 w-12 items-center justify-center bg-navy text-brand-green">
              <Building2 size={22} strokeWidth={1.7} />
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Referrals</h3>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 max-w-md text-lg leading-relaxed text-steel">
              We are privileged to have earned the honor to serve some of the region's most discerning companies.
            </p>
          </Reveal>
        </div>

        <div className="grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2">
          {REFERRALS.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.06} className="bg-white">
              <div className="flex h-full flex-col justify-center p-6">
                <p className="font-display text-lg font-bold tracking-tight text-navy">{r.name}</p>
                {r.region && <p className="mt-1 text-sm text-steel">{r.region}</p>}
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-16 border-t border-border pt-10">
        <Reveal>
          <span className="overline">Business Accreditations</span>
        </Reveal>
        <div className="mt-6 flex flex-wrap gap-3">
          {ACCREDITATIONS.map((a, i) => (
            <Reveal key={a} delay={i * 0.06}>
              <span className="inline-flex items-center gap-2 border border-navy/15 px-5 py-3 font-display text-sm font-semibold tracking-tight text-navy">
                <BadgeCheck size={16} className="text-brand-green-ink" />
                {a}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default function WhyPrecision() {
  return (
    <>
      <Seo
        title="Why Precision"
        description="Thirty-seven years of established experience, referral-based hiring, screening and reliability, site-specific training, and licensed, bonded and insured service."
        path="/why-precision"
      />
      <PageHero eyebrow="Why Precision" title={["Built on people,", "proven over decades."]} />

      {SECTIONS.map((section, i) => (
        <CredibilityRow key={section.title} section={section} index={i} />
      ))}

      <PeopleAndValues />

      <CtaBand
        title="Request a Customized Cleaning Plan"
        body=""
        primary={{ label: "Request a Customized Cleaning Plan", to: "/request-a-quote" }}
        secondary={{ label: "Talk With Our Team", to: "/contact" }}
      />
    </>
  );
}
